import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dumbbell, Activity, TrendingUp, Trophy, Calendar, ChevronDown, ChevronUp, Info } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { ExerciseHistoryModal } from "./ExerciseHistoryModal";
import { ExerciseInfoModal } from "./ExerciseInfoModal";

interface ExerciseStats {
  name: string;
  workout_type: string;
  sessions: number;
  last_workout: string;
  current_record: {
    value: number;
    unit: string;
    type: string;
  } | null;
  recent_performance: string;
}

export const ExerciseList = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [exercises, setExercises] = useState<ExerciseStats[]>([]);
  const [selectedExercise, setSelectedExercise] = useState<string>("");
  const [modalOpen, setModalOpen] = useState(false);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [selectedExerciseForInfo, setSelectedExerciseForInfo] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (user) {
      loadExercises();
    }
  }, [user]);

  const loadExercises = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('exercises')
        .select(`
          name,
          sets,
          reps,
          weight_kg,
          distance_km,
          duration_seconds,
          workouts!inner(
            completed_at,
            workout_type,
            user_id
          )
        `)
        .eq('workouts.user_id', user.id)
        .not('workouts.completed_at', 'is', null)
        .order('completed_at', { referencedTable: 'workouts', ascending: false });

      if (error) throw error;

      // Group exercises and calculate stats
      const exerciseMap = new Map<string, any[]>();
      
      (data || []).forEach(exercise => {
        const key = exercise.name;
        if (!exerciseMap.has(key)) {
          exerciseMap.set(key, []);
        }
        exerciseMap.get(key)?.push({
          ...exercise,
          workout_date: exercise.workouts.completed_at,
          workout_type: exercise.workouts.workout_type
        });
      });

      const exerciseStats: ExerciseStats[] = Array.from(exerciseMap.entries()).map(([name, sessions]) => {
        const latestSession = sessions[0];
        const isRunning = latestSession.workout_type === 'running' || name.toLowerCase().includes('running');
        
        // Calculate current record
        let current_record = null;
        if (isRunning) {
          const maxDistance = Math.max(...sessions.filter(s => s.distance_km).map(s => s.distance_km));
          if (maxDistance > 0) {
            current_record = {
              value: maxDistance,
              unit: 'km',
              type: 'Max Distance'
            };
          }
        } else {
          const maxWeight = Math.max(...sessions.filter(s => s.weight_kg).map(s => s.weight_kg));
          if (maxWeight > 0) {
            current_record = {
              value: maxWeight,
              unit: 'kg',
              type: 'Max Weight'
            };
          }
        }

        // Calculate recent performance
        let recent_performance = '';
        if (isRunning) {
          if (latestSession.distance_km) {
            recent_performance = `${latestSession.distance_km}km`;
            if (latestSession.duration_seconds) {
              const pace = (latestSession.duration_seconds / 60) / latestSession.distance_km;
              recent_performance += ` @ ${pace.toFixed(2)} min/km`;
            }
          }
        } else {
          const parts = [];
          if (latestSession.weight_kg) parts.push(`${latestSession.weight_kg}kg`);
          if (latestSession.sets) parts.push(`${latestSession.sets} sets`);
          if (latestSession.reps) parts.push(`${latestSession.reps} reps`);
          recent_performance = parts.join(' × ');
        }

        return {
          name,
          workout_type: latestSession.workout_type,
          sessions: sessions.length,
          last_workout: latestSession.workout_date,
          current_record,
          recent_performance
        };
      });

      // Sort by most sessions (most done exercises first), then by last workout date
      exerciseStats.sort((a, b) => {
        if (b.sessions !== a.sessions) {
          return b.sessions - a.sessions;
        }
        return new Date(b.last_workout).getTime() - new Date(a.last_workout).getTime();
      });
      
      setExercises(exerciseStats);
    } catch (error) {
      console.error('Error loading exercises:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExerciseClick = (exerciseName: string) => {
    setSelectedExercise(exerciseName);
    setModalOpen(true);
  };

  const handleInfoClick = (exerciseName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedExerciseForInfo(exerciseName);
    setInfoModalOpen(true);
  };

  if (loading) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        {t('components.exerciseList.loading')}
      </div>
    );
  }

  if (exercises.length === 0) {
    return (
      <Card className="bg-gradient-card border-0">
        <CardContent className="text-center py-8">
          <Dumbbell className="h-12 w-12 mx-auto mb-4 opacity-50 text-muted-foreground" />
          <p className="text-muted-foreground">{t('components.exerciseList.empty')}</p>
          <p className="text-sm text-muted-foreground">{t('components.exerciseList.emptyHint')}</p>
        </CardContent>
      </Card>
    );
  }

  const displayedExercises = showAll ? exercises : exercises.slice(0, 3);
  const hasMoreExercises = exercises.length > 3;

  return (
    <>
      <Card className="bg-gradient-card border-0">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-normal">
            <TrendingUp className="h-5 w-5 text-primary" />
            {t('components.exerciseList.title', { count: exercises.length })}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {displayedExercises.map((exercise) => (
            <div 
              key={exercise.name}
              onClick={() => handleExerciseClick(exercise.name)}
              className="p-3 sm:p-4 rounded-2xl backdrop-blur-xl bg-white/[0.03] hover:bg-white/[0.05] cursor-pointer transition-all duration-200 shadow-[inset_0_1px_1px_rgba(255,255,255,0.04),0_4px_24px_rgba(0,0,0,0.2)]"
            >
              {/* Header Row - Name, Badge, Record */}
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary/10 flex-shrink-0">
                    {exercise.workout_type === 'running' || exercise.name.toLowerCase().includes('running') ? (
                      <Activity className="h-4 w-4 sm:h-5 sm:w-5 text-accent" />
                    ) : (
                      <Dumbbell className="h-4 w-4 sm:h-5 sm:w-5 text-secondary" />
                    )}
                  </div>
                  <h4 className="font-semibold text-sm sm:text-base truncate">{exercise.name}</h4>
                </div>
                
                {exercise.current_record && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Trophy className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-yellow-500" />
                    <span className="text-xs sm:text-sm font-semibold whitespace-nowrap">
                      {exercise.current_record.value}
                      <span className="text-[10px] sm:text-xs text-muted-foreground ml-0.5">
                        {exercise.current_record.unit}
                      </span>
                    </span>
                  </div>
                )}
              </div>

              {/* Info Row - Sessions, Latest Performance */}
              <div className="flex items-center justify-between gap-3 mb-2 ml-10 sm:ml-12">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Badge variant="outline" className="text-[10px] sm:text-xs whitespace-nowrap flex-shrink-0">
                    {t('components.exerciseList.sessionsCount', { count: exercise.sessions })}
                  </Badge>
                  {exercise.recent_performance && (
                    <span className="text-xs sm:text-sm text-muted-foreground truncate">
                      {exercise.recent_performance}
                    </span>
                  )}
                </div>
              </div>

              {/* Footer Row - Date and Actions */}
              <div className="flex items-center justify-between gap-2 ml-10 sm:ml-12">
                <div className="flex items-center gap-1 text-[10px] sm:text-xs text-muted-foreground flex-shrink-0">
                  <Calendar className="h-3 w-3" />
                  {new Date(exercise.last_workout).toLocaleDateString()}
                </div>
                
                <div className="flex items-center gap-1.5">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => handleInfoClick(exercise.name, e)}
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-primary"
                  >
                    <Info className="h-3.5 w-3.5" />
                  </Button>
                  
                  <Badge 
                    variant="secondary" 
                    className="text-[10px] sm:text-xs bg-primary/10 text-primary border-primary/20 whitespace-nowrap px-2 py-0.5"
                  >
                    {t('components.exerciseList.view')}
                  </Badge>
                </div>
              </div>
            </div>
          ))}
          
          {hasMoreExercises && (
            <div className="pt-4">
              <Button
                variant="ghost"
                onClick={() => setShowAll(!showAll)}
                className="w-full text-primary hover:text-primary hover:bg-primary/10"
              >
                {showAll ? (
                  <>
                    <ChevronUp className="h-4 w-4 mr-2" />
                    {t('components.exerciseList.showLess')}
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4 mr-2" />
                    {t('components.exerciseList.seeAllMore', { count: exercises.length - 3 })}
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <ExerciseHistoryModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        exerciseName={selectedExercise}
      />

      <ExerciseInfoModal
        isOpen={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
        exerciseName={selectedExerciseForInfo}
      />
    </>
  );
};